// Cada recusa da trava, traduzida em CAUSA e PRÓXIMA AÇÃO.
//
// A tela tinha `setErro((e as Error).message)` e mostrava o texto do servidor
// cru. Os códigos são cinco, e significam coisas operacionalmente diferentes —
// um pede medir de novo, outro pede esperar a fonte voltar, outro não é nem
// recusa da trava. Mostrar todos como um parágrafo vermelho fazia o operador
// tratá-los igual: clicar de novo.
//
// A regra que ordena este arquivo: quem lê precisa saber O QUE FAZER A SEGUIR.
// Uma causa sem próxima ação é um beco.

export interface ErroTraduzido {
  causa: string;
  proximaAcao: string;
  /** `true` quando NÃO é recusa da trava — muda o tom da mensagem na tela. */
  naoEhRecusaDaTrava?: boolean;
}

/** O que o SDK entrega quando a API responde um envelope de erro. */
interface ErroDaApi {
  code?: string;
  message?: string;
  details?: unknown;
}

function envelope(e: unknown): ErroDaApi {
  if (typeof e !== "object" || e === null) return {};
  const obj = e as Record<string, unknown>;
  // O SDK expõe o envelope em `code`/`details`; alguns caminhos aninham em `error`.
  const err = (obj.error ?? obj) as Record<string, unknown>;
  return {
    code: typeof err.code === "string" ? err.code : undefined,
    message: typeof err.message === "string" ? err.message : (obj.message as string | undefined),
    details: err.details,
  };
}

const MINUTOS_DE_VALIDADE = 10;

const POR_CODIGO: Record<string, ErroTraduzido> = {
  medicao_invalida: {
    causa: "Esta unidade não pôde ser medida agora.",
    proximaAcao:
      "Veja o detalhe abaixo: costuma ser uma matriz que exige escolher a filial, ou a fonte de dados fora do ar. Desligar continua disponível.",
  },
  fonte_nao_autoritativa: {
    causa: "A medição não veio do banco de dados autoritativo.",
    proximaAcao:
      "Ligar exige medição vinda do PostgreSQL. Aguarde a fonte voltar e consulte o impacto de novo — desligar não depende disso.",
  },
  medicao_desatualizada: {
    causa: `A medição exibida passou de ${MINUTOS_DE_VALIDADE} minutos.`,
    proximaAcao:
      "Consulte o impacto de novo antes de ligar. Uma carga pode ter entrado nesse intervalo e mudado quantos vínculos seriam bloqueados.",
  },
  medicao_divergente: {
    causa: "O número que estava na tela não é mais o número medido.",
    proximaAcao: "Consulte o impacto de novo e confira o novo total antes de confirmar.",
  },
  parametro_invalido: {
    causa: "Um dos filtros está em formato inválido.",
    proximaAcao:
      "Corrija o campo indicado no detalhe. Competência é AAAAMM (ex.: 202608) e datas são dia a dia.",
  },
  persistencia_falhou: {
    causa: "A operação não pôde ser registrada.",
    proximaAcao:
      "Isto não é uma recusa da trava: a operação foi interrompida porque não daria para registrá-la. Tente de novo; persistindo, acione o suporte.",
    naoEhRecusaDaTrava: true,
  },
  validation_error: {
    causa: "Faltou preencher algo obrigatório.",
    proximaAcao: "Confira a justificativa: ela é obrigatória e precisa ter ao menos 3 caracteres.",
  },
  forbidden: {
    causa: "Seu usuário não tem a permissão de folhas.",
    proximaAcao:
      "Peça acesso a quem administra os perfis. Sem essa permissão não é possível ligar nem desligar a trava.",
  },
};

const DESCONHECIDO: ErroTraduzido = {
  causa: "A operação não foi concluída.",
  proximaAcao: "Tente de novo. Se repetir, informe ao suporte o horário e a unidade.",
};

/**
 * Traduz o erro de uma operação da trava.
 *
 * Devolve SEMPRE algo utilizável — um código desconhecido cai no genérico em vez
 * de deixar a tela sem explicação, que era o comportamento anterior.
 */
export function traduzirErroDaTrava(e: unknown): ErroTraduzido & { detalheTecnico?: string } {
  const { code, message } = envelope(e);
  const base = (code && POR_CODIGO[code]) || DESCONHECIDO;
  // A mensagem do servidor vira DETALHE, nunca a explicação principal.
  return { ...base, detalheTecnico: message };
}

/**
 * A frase da divergência, com os dois números.
 *
 * Existe separada porque é o único caso em que a próxima ação depende de dados
 * da resposta: dizer só "o número mudou" deixa o operador sem saber se mudou
 * para mais ou para menos, que é o que decide se ele ainda quer ligar.
 */
export function frasearDivergencia(e: unknown): string | null {
  const { code, details } = envelope(e);
  if (code !== "medicao_divergente" || typeof details !== "object" || details === null) return null;
  const d = details as { confirmado?: number; medido?: number };
  if (typeof d.confirmado !== "number" || typeof d.medido !== "number") return null;
  return `A tela mostrava ${d.confirmado} vínculo(s) a bloquear; a medição atual acusa ${d.medido}.`;
}

/** Quantos milissegundos a medição ainda vale. Negativo = expirada. */
export const VALIDADE_MEDICAO_MS = MINUTOS_DE_VALIDADE * 60_000;

/** Faltando isto ou menos, a tela alerta antes de o operador tentar confirmar. */
export const ALERTA_VALIDADE_MS = 2 * 60_000;

export type EstadoValidade = "sem-medicao" | "valida" | "expirando" | "expirada";

/**
 * Estado da medição a partir do instante em que ela foi PRODUZIDA.
 *
 * Deriva de `medido_em`, e não de quando a tela renderizou: o relógio começa a
 * correr no servidor, e mostrar o tempo desde o render daria ao operador mais
 * prazo do que ele tem.
 */
export function estadoDaValidade(
  medidoEm: string | null | undefined,
  agora: number = Date.now(),
): {
  estado: EstadoValidade;
  restanteMs: number;
} {
  if (!medidoEm) return { estado: "sem-medicao", restanteMs: 0 };
  const t = Date.parse(medidoEm);
  if (!Number.isFinite(t)) return { estado: "sem-medicao", restanteMs: 0 };
  const restanteMs = VALIDADE_MEDICAO_MS - (agora - t);
  if (restanteMs <= 0) return { estado: "expirada", restanteMs: 0 };
  if (restanteMs <= ALERTA_VALIDADE_MS) return { estado: "expirando", restanteMs };
  return { estado: "valida", restanteMs };
}

/** "7 min", "45 s" — o que cabe ao lado de um botão. */
export function formatarRestante(restanteMs: number): string {
  if (restanteMs <= 0) return "expirada";
  const seg = Math.ceil(restanteMs / 1000);
  return seg >= 60 ? `${Math.floor(seg / 60)} min` : `${seg} s`;
}
